import React, { useState, useEffect } from "react";
import API_URL from "../../common/config2";
import {
  FaCreditCard,
  FaCheckCircle,
  FaRegCreditCard,
  FaShieldAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import axios from "axios";
import DASHBOARD_URL from "../../common/Redirect";

// Main Payment Component with Real Payment Processing
export default function Step4Payment({
  formData,
  isLoading,
  paymentError,
  onPaymentComplete,
  selectedPlan,
  setCurrentStep,
  setIsLoading,
  setMessage,
  setStepsValidated,
  setErrors,
  errors,
  csrfToken,
  onSessionExpired,
}) {
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [error, setError] = useState("");
  const [cardDetails, setCardDetails] = useState({
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    cardholderName: "",
  });

  const [cardErrors, setCardErrors] = useState({});

  const planDetails = {
    name: selectedPlan?.name || "Alpha Plan",
    price: selectedPlan?.price || 0,
    duration: selectedPlan?.duration || "30 days",
    features: selectedPlan?.features || [
      "All UIM platform features",
      "12-month access",
      "Priority support",
      "Startup marketplace access",
    ],
  };

  // Validate card details
  const validateCardDetails = () => {
    const errors = {};

    // Card number validation (basic)
    if (!cardDetails.cardNumber.replace(/\s/g, "")) {
      errors.cardNumber = "Card number is required";
    } else if (cardDetails.cardNumber.replace(/\s/g, "").length < 13) {
      errors.cardNumber = "Card number must be at least 13 digits";
    }

    // Expiry date validation
    if (!cardDetails.expiryDate) {
      errors.expiryDate = "Expiry date is required";
    } else if (!/^\d{2}\/\d{2}$/.test(cardDetails.expiryDate)) {
      errors.expiryDate = "Expiry date must be in MM/YY format";
    }

    // CVV validation
    if (!cardDetails.cvv) {
      errors.cvv = "CVV is required";
    } else if (cardDetails.cvv.length < 3) {
      errors.cvv = "CVV must be at least 3 digits";
    }

    // Cardholder name validation
    if (!cardDetails.cardholderName.trim()) {
      errors.cardholderName = "Cardholder name is required";
    }

    return errors;
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  // Format expiry date
  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return v.substring(0, 2) + "/" + v.substring(2, 4);
    }
    return v;
  };

  // Handle input changes
  const handleCardInputChange = (field, value) => {
    let formattedValue = value;

    if (field === "cardNumber") {
      formattedValue = formatCardNumber(value);
    } else if (field === "expiryDate") {
      formattedValue = formatExpiryDate(value);
    } else if (field === "cvv") {
      formattedValue = value.replace(/[^0-9]/g, "").substring(0, 4);
    }

    setCardDetails((prev) => ({
      ...prev,
      [field]: formattedValue,
    }));

    // Clear specific field error
    if (cardErrors[field]) {
      setCardErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle subscription completion
  const handleCompleteSubscription = async () => {
    if (!csrfToken) {
      setError(
        "Security token is missing. Please wait while we refresh your session."
      );
      if (onSessionExpired) onSessionExpired();
      return;
    }

    // For free plans, skip payment validation
    const isFree =
      planDetails.price === 0 ||
      planDetails.price === "0" ||
      planDetails.price === "Free";

    if (!isFree) {
      // Validate card details for paid plans
      const validationErrors = validateCardDetails();
      if (Object.keys(validationErrors).length > 0) {
        setCardErrors(validationErrors);
        setError("Please correct the card details and try again.");
        return;
      }
    }

    setPaymentProcessing(true);
    setError("");
    setCardErrors({});

    if (!formData.startupId || !formData.selectedPlan) {
      console.error("Missing formData for subscription:", formData);
      setPaymentProcessing(false);
      setError(
        "Missing required information. Please go back and complete previous steps."
      );
      return;
    }

    try {
      let paymentIntentId = null;

      // Process payment for paid plans
      if (!isFree) {
        // Create payment intent
        const paymentResponse = await axios.post(
          `${API_URL}/payments/create-payment-intent`,
          {
            // amount: planDetails.price * 100, // Convert to cents
            // currency: "usd",
            // startupId: formData.startupId,
            planId: formData.selectedPlan,
            email: formData.email,
            // cardDetails: {
            //   number: cardDetails.cardNumber.replace(/\s/g, ""),
            //   exp_month: cardDetails.expiryDate.split("/")[0],
            //   exp_year: "20" + cardDetails.expiryDate.split("/")[1],
            //   cvc: cardDetails.cvv,
            //   name: cardDetails.cardholderName,
            // },
          },
          {
            headers: {
              "x-csrf-token": csrfToken,
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );

        if (paymentResponse.data.success) {
          paymentIntentId = paymentResponse.data.paymentIntentId;
          if (paymentResponse.data.isFree) {
            // Handle free plan
            paymentIntentId = null;
          } else {
            paymentIntentId = paymentResponse.data.paymentIntentId;
          }
        } else {
          throw new Error(
            paymentResponse.data.error || "Payment processing failed"
          );
        }
      }

      // Create subscription
      // const response = await axios.post(
      //   `${API_URL}/subscriptions`,
      //   {
      //     startupId: formData.startupId,
      //     planId: formData.selectedPlan,
      //     paymentIntentId: paymentIntentId,
      //   },
      //   {
      //     headers: {
      //       "x-csrf-token": csrfToken,
      //       "Content-Type": "application/json",
      //     },
      //     withCredentials: true,
      //   }
      // );

      // console.log("Subscription Response:", response);

      // if (response.status === 201 || response.status === 200) {
      //   const paymentData = {
      //     success: true,
      //     data: {
      //       id: paymentIntentId || "free_plan_" + Date.now(),
      //       amount: isFree ? 0 : planDetails.price,
      //       plan: formData.selectedPlan,
      //       startupId: formData.startupId,
      //     },
      //   };

      //   console.log("Payment Data:", paymentData);
      //   onPaymentComplete(paymentData);
      // } else {
      //   throw new Error("Unexpected response from server");
      // }

      try {
        // Create a bypass subscription for all plans
        const response = await axios.post(
          `${API_URL}/subscriptions`,
          {
            startupId: formData.startupId,
            planId: formData.selectedPlan,
            paymentIntentId: paymentIntentId,
          },
          {
            headers: {
              "x-csrf-token": csrfToken,
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );

        console.log("Subscription Response:", response);

        if (response.status === 201 || response.status === 200) {
          let successMessage = "Registration completed successfully!";
          if (
            selectedPlan.price === 0 ||
            selectedPlan.price === "0" ||
            selectedPlan.price === "Free"
          ) {
            successMessage = "Free plan subscription activated successfully!";
          } else {
            successMessage = "Subscription created successfully!";
          }
          const paymentData = {
            success: true,
            data: {
              id: paymentIntentId || "free_plan_" + Date.now(),
              amount: isFree ? 0 : planDetails.price,
              plan: formData.selectedPlan,
              startupId: formData.startupId,
            },
          };

          console.log("Payment Data:", paymentData);
          onPaymentComplete(paymentData);

          setMessage(successMessage);
          setStepsValidated((prev) => ({ ...prev, step4: true }));
          setCurrentStep(5);
          // setTimeout(() => navigate("/dashboard"), 3001);
          const encodedUserData = encodeURIComponent(
            JSON.stringify(localStorage.getItem("startUpData"))
          );
          const encodedToken = encodeURIComponent(
            localStorage.getItem("token")
          );
          const encodedRefreshToken = encodeURIComponent(
            localStorage.getItem("refreshToken")
          );
          const userId = localStorage.getItem("startUpId");

          window.location.href = `${DASHBOARD_URL}/dashboard?userData=${encodedUserData}&userId=${userId}&token=${encodedToken}&refreshToken=${encodedRefreshToken}`;
        } else {
          setErrors({
            general: "Unexpected response from server. Please try again.",
          });
        }
      } catch (error) {
        console.error("Subscription Error:", error);

        // Handle session expired error
        if (
          error.response?.status === 403 &&
          (error.response?.data?.error?.includes("CSRF token") ||
            error.response?.data?.error?.includes("session expired"))
        ) {
          return;
        }

        let errorMessage = "Registration failed. Please try again.";

        if (error.response?.status === 400) {
          errorMessage = "Invalid data. Please try again.";
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }

        setErrors({ general: errorMessage });
      } finally {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Subscription Error:", error);
      setPaymentProcessing(false);
      // Handle different types of errors
      if (error.response?.status === 403) {
        if (
          error.response?.data?.error?.includes("CSRF token") ||
          error.response?.data?.error?.includes("session expired")
        ) {
          setError(
            "Your session has expired. Please wait while we refresh your session."
          );
          if (onSessionExpired) onSessionExpired();
          return;
        }
      }
      // Handle payment specific errors
      if (error.response?.status === 402) {
        setError(
          "Payment was declined. Please check your card details and try again."
        );
        console.log(error.response);
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.error;
        if (errorMsg?.includes("card")) {
          setError("Invalid card details. Please check and try again.");
        } else {
          setError(
            errorMsg || "Invalid request. Please check your information."
          );
        }
      } else if (error.response?.status === 429) {
        setError("Too many attempts. Please wait a moment and try again.");
      } else if (error.response?.status >= 500) {
        setError("Server error. Please try again later or contact support.");
      } else {
        setError(
          error.response?.data?.error ||
            error.message ||
            "Failed to process your registration. Please try again."
        );
      }
    } finally {
      setPaymentProcessing(false);
    }
  };

  useEffect(() => {
    console.log("Selected Plan:", selectedPlan);
    console.log("Plan Details:", planDetails);
    console.log("Form Data:", formData);
    console.log("CSRF Token:", csrfToken ? "Present" : "Missing");
  }, [csrfToken]);

  const isFree =
    planDetails.price === 0 ||
    planDetails.price === "0" ||
    planDetails.price === "Free";

  return (
    <>
      <div className="sec-heading centered">
        <div className="content-area">
          <h2
            className="title wow fadeInUp"
            style={{
              fontSize: "2.2rem",
              color: "#060922",
              fontWeight: "700",
              marginBottom: "15px",
            }}
            data-wow-delay=".4s">
            Complete Your Registration
          </h2>
          <p
            className="subtitle-text wow fadeInUp"
            style={{
              fontSize: "1.1rem",
              color: "#666",
              maxWidth: "80%",
              margin: "0 auto 20px",
            }}>
            Review your plan selection and{" "}
            {!isFree ? "enter payment details" : "confirm your subscription"}
          </p>
        </div>
      </div>

      <div className="payment-form-inputs wow fadeInUp" data-wow-delay=".6s">
        <div
          className="custom-form-area input-boxed"
          style={{
            padding: "30px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
          }}>
          {/* Display session status message if no token */}
          {!csrfToken && (
            <div
              className="alert alert-warning mb-4"
              role="alert"
              style={{ borderRadius: "8px" }}>
              <div className="d-flex align-items-center">
                <FaExclamationTriangle
                  className="me-3"
                  style={{ fontSize: "1.5rem", color: "#f0ad4e" }}
                />
                <div>
                  <h5
                    className="alert-heading mb-1"
                    style={{ fontSize: "1rem", fontWeight: "600" }}>
                    Session Initializing
                  </h5>
                  <p className="mb-0" style={{ fontSize: "0.9rem" }}>
                    Please wait while we establish your secure session...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Display error message if exists */}
          {(error || paymentError) && (
            <div
              className="alert alert-danger mb-4"
              role="alert"
              style={{ borderRadius: "8px" }}>
              <i className="fas fa-exclamation-circle me-2"></i>
              {error || paymentError}
            </div>
          )}

          <div className="order-summary mb-4">
            <div
              className="card"
              style={{
                boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                borderRadius: "12px",
                border: "none",
              }}>
              <div
                className="card-header"
                style={{
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  backgroundColor: "#f8f9fa",
                  borderBottom: "2px solid #09aff4",
                  padding: "15px 20px",
                }}>
                <h5
                  className="mb-0 d-flex align-items-center"
                  style={{ color: "#060922", fontWeight: "600" }}>
                  <FaRegCreditCard
                    style={{ marginRight: "10px", color: "#09aff4" }}
                  />
                  Order Summary
                </h5>
              </div>
              <div className="card-body" style={{ padding: "20px" }}>
                <div
                  className="d-flex justify-content-between align-items-center p-3 mb-3"
                  style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontWeight: "500", fontSize: "1.1rem" }}>
                    Selected Plan
                  </span>
                  <strong
                    style={{
                      fontSize: "1.1rem",
                      backgroundColor: "#e6f7ff",
                      padding: "5px 15px",
                      borderRadius: "20px",
                      color: "#09aff4",
                    }}>
                    {planDetails.name}
                  </strong>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between mb-3">
                      <span style={{ color: "#666" }}>Duration</span>
                      <strong style={{ color: "#060922" }}>
                        {planDetails.duration}
                      </strong>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between mb-3">
                      <span style={{ color: "#666" }}>Amount</span>
                      <strong style={{ color: "#060922" }}>
                        {isFree ? "Free" : `$${planDetails.price}.00`}
                      </strong>
                    </div>
                  </div>
                </div>

                <hr className="my-4" style={{ borderColor: "#e9ecef" }} />

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <strong style={{ fontSize: "1.2rem", color: "#060922" }}>
                    Total
                  </strong>
                  <strong
                    className="text-primary"
                    style={{
                      fontSize: "1.2rem",
                      color: "#09aff4",
                      backgroundColor: isFree ? "#d1f8e9" : "#e6f7ff",
                      padding: "8px 20px",
                      borderRadius: "8px",
                      fontWeight: "700",
                    }}>
                    {isFree ? "Free" : `$${planDetails.price}.00`}
                  </strong>
                </div>

                <div
                  className="mt-3 p-3"
                  style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                  <div className="d-flex align-items-center mb-2">
                    <FaCheckCircle
                      style={{ color: "#28a745", marginRight: "8px" }}
                    />
                    <strong style={{ color: "#060922" }}>
                      Includes access to:
                    </strong>
                  </div>
                  <ul className="mt-2 mb-0" style={{ paddingLeft: "25px" }}>
                    {planDetails.features.map((feature, index) => (
                      <li
                        key={index}
                        style={{ marginBottom: "8px", color: "#555" }}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Details for Paid Plans */}
          {!isFree && (
            <div className="mb-4">
              <div
                className="card"
                style={{
                  boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                  borderRadius: "12px",
                  border: "none",
                }}>
                <div
                  className="card-header"
                  style={{
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #09aff4",
                    padding: "15px 20px",
                  }}>
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{ color: "#060922", fontWeight: "600" }}>
                    <FaCreditCard
                      style={{ marginRight: "10px", color: "#09aff4" }}
                    />
                    Payment Details
                  </h5>
                </div>
                <div className="card-body" style={{ padding: "20px" }}>
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label style={{ fontWeight: "500", marginBottom: "8px" }}>
                        Cardholder Name
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          cardErrors.cardholderName ? "is-invalid" : ""
                        }`}
                        placeholder="John Doe"
                        value={cardDetails.cardholderName}
                        onChange={(e) =>
                          handleCardInputChange(
                            "cardholderName",
                            e.target.value
                          )
                        }
                        style={{ borderRadius: "8px", padding: "12px" }}
                      />
                      {cardErrors.cardholderName && (
                        <div className="invalid-feedback">
                          {cardErrors.cardholderName}
                        </div>
                      )}
                    </div>
                    <div className="col-md-12 mb-3">
                      <label style={{ fontWeight: "500", marginBottom: "8px" }}>
                        Card Number
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          cardErrors.cardNumber ? "is-invalid" : ""
                        }`}
                        placeholder="1234 5678 9012 3456"
                        value={cardDetails.cardNumber}
                        onChange={(e) =>
                          handleCardInputChange("cardNumber", e.target.value)
                        }
                        maxLength={19}
                        style={{ borderRadius: "8px", padding: "12px" }}
                      />
                      {cardErrors.cardNumber && (
                        <div className="invalid-feedback">
                          {cardErrors.cardNumber}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label style={{ fontWeight: "500", marginBottom: "8px" }}>
                        Expiry Date
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          cardErrors.expiryDate ? "is-invalid" : ""
                        }`}
                        placeholder="MM/YY"
                        value={cardDetails.expiryDate}
                        onChange={(e) =>
                          handleCardInputChange("expiryDate", e.target.value)
                        }
                        maxLength={5}
                        style={{ borderRadius: "8px", padding: "12px" }}
                      />
                      {cardErrors.expiryDate && (
                        <div className="invalid-feedback">
                          {cardErrors.expiryDate}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label style={{ fontWeight: "500", marginBottom: "8px" }}>
                        CVV
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          cardErrors.cvv ? "is-invalid" : ""
                        }`}
                        placeholder="123"
                        value={cardDetails.cvv}
                        onChange={(e) =>
                          handleCardInputChange("cvv", e.target.value)
                        }
                        maxLength={4}
                        style={{ borderRadius: "8px", padding: "12px" }}
                      />
                      {cardErrors.cvv && (
                        <div className="invalid-feedback">{cardErrors.cvv}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-center">
            {isLoading || paymentProcessing ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-4">
                <div
                  className="spinner-border mb-3"
                  style={{ color: "#09aff4", width: "3rem", height: "3rem" }}
                  role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p style={{ color: "#060922", fontSize: "1.1rem" }}>
                  {isFree
                    ? "Processing your registration..."
                    : "Processing payment..."}
                </p>
              </div>
            ) : (
              <div>
                <div
                  className="alert mb-4"
                  style={{
                    backgroundColor: isFree ? "#d1f8e9" : "#e6f7ff",
                    color: isFree ? "#0b6e4f" : "#09aff4",
                    borderColor: isFree ? "#aaeacd" : "#b3e0ff",
                    borderRadius: "8px",
                    padding: "15px",
                    fontSize: "1.05rem",
                  }}>
                  <i
                    className={`fas ${
                      isFree ? "fa-check-circle" : "fa-lock"
                    } me-2`}></i>
                  {isFree
                    ? "You've selected the free plan. No payment required."
                    : "Your payment information is secure and encrypted."}
                </div>

                <button
                  type="button"
                  className="btn-solid"
                  onClick={handleCompleteSubscription}
                  disabled={!csrfToken || isLoading || paymentProcessing}
                  style={{
                    backgroundColor: "#09aff4",
                    color: "#fff",
                    border: "none",
                    padding: "16px 30px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "1.1rem",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 15px rgba(9, 175, 244, 0.3)",
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                    opacity: !csrfToken ? "0.7" : "1",
                  }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    {isFree ? (
                      <>
                        Complete Registration{" "}
                        <FaCheckCircle style={{ marginLeft: "10px" }} />
                      </>
                    ) : (
                      <>
                        Complete Payment & Registration{" "}
                        <FaCreditCard style={{ marginLeft: "10px" }} />
                      </>
                    )}
                  </span>
                </button>

                <p
                  className="mt-4 text-center"
                  style={{
                    color: "#666",
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  <FaShieldAlt
                    style={{ color: "#09aff4", marginRight: "8px" }}
                  />
                  By proceeding, you agree to the terms of service and privacy
                  policy.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
